import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";

type PageVisibilityContextType = {
  isVisible: boolean;
};

const PageVisibilityContext = createContext<PageVisibilityContextType | undefined>(undefined);

type PageVisibilityProviderProps = {
  children: ReactNode;
};

export function PageVisibilityProvider({ children }: PageVisibilityProviderProps) {
  const [isVisible, setIsVisible] = useState(() => document.visibilityState === "visible");

  useEffect(() => {
    const onVisibilityChange = () => {
      setIsVisible(document.visibilityState === "visible");
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  const contextValue = useMemo(() => ({ isVisible }), [isVisible]);

  return <PageVisibilityContext.Provider value={contextValue}>{children}</PageVisibilityContext.Provider>;
}

// Hook to use the context
export function usePageVisibility(): boolean {
  const context = useContext(PageVisibilityContext);

  if (context === undefined) {
    throw new Error("usePageVisibility must be used within a PageVisibilityProvider");
  }

  return context.isVisible;
}
